import { gql } from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useMeContext } from '~/modules/me/useMeContext';
import { mapScaleToPeriodResolution } from '~/modules/resourcing/common/util';

export const PROJECT_ACTUAL_SERIES_BY_TASK_QUERY = gql`
  query getResourceUserProjectActualSeriesByTask(
    $projectId: String!
    $userUri: String!
    $dateRange: DateRangeInput!
    $periodResolution: PeriodResolutionOption!
    $taskUris: [String!]
  ) {
    resourceUsers2(
      page: 1
      pagesize: 1
      filter: { userIds: [$userUri], includeDisabled: true }
    ) {
      id
      projectActualSeriesByTask(
        projectUri: $projectId
        dateRange: $dateRange
        periodResolution: $periodResolution
        taskUris: $taskUris
      ) {
        task {
          id
          displayText
        }
        periods {
          hours {
            hours
            minutes
            seconds
          }
          dateRange {
            startDate: startDate2
            endDate: endDate2
          }
        }
      }
    }
  }
`;

export const PROJECT_TASK_ACTUAL_SERIES_BY_TASK_FOR_RESOURCE_USER_QUERY = gql`
  query getProjectActualSeriesByTaskForResourceUser(
    $projectId: String!
    $userUri: String!
    $dateRange: DateRangeInput!
    $periodResolution: PeriodResolutionOption!
    $taskUris: [String!]
  ) {
    projectActualSeriesByTaskForResourceUser(
      input: {
        projectId: $projectId
        userUri: $userUri
        dateRange: $dateRange
        periodResolution: $periodResolution
        taskUris: $taskUris
      }
    ) {
      task {
        id
        displayText
      }
      periods {
        hours {
          hours
          minutes
          seconds
        }
        dateRange {
          startDate: startDate2
          endDate: endDate2
        }
      }
    }
  }
`;

export const mapProjectActualSeriesByTaskToMap = projectActualSeriesByTask =>
  (projectActualSeriesByTask || []).reduce((retVal, current) => {
    // eslint-disable-next-line no-param-reassign
    retVal[current.task.id] = current;

    return retVal;
  }, {});

const useLegacyProjectActualSeriesByTaskForUser = ({
  dateRange,
  projectId,
  periodResolution,
  taskUris,
  userUri,
  skip
}) => {
  const { data, loading } = useQuery(PROJECT_ACTUAL_SERIES_BY_TASK_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      dateRange,
      userUri,
      projectId,
      periodResolution,
      taskUris
    },
    skip
  });

  return {
    loading,
    projectActualSeriesByTask: mapProjectActualSeriesByTaskToMap(
      data?.resourceUsers2?.[0]?.projectActualSeriesByTask
    )
  };
};

const useProjectActualSeriesByTaskForResourceUser = ({
  dateRange,
  projectId,
  periodResolution,
  taskUris,
  userUri,
  skip
}) => {
  const { data, loading } = useQuery(
    PROJECT_TASK_ACTUAL_SERIES_BY_TASK_FOR_RESOURCE_USER_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        dateRange,
        userUri,
        projectId,
        periodResolution,
        taskUris
      },
      skip
    }
  );

  return {
    loading,
    projectActualSeriesByTask: mapProjectActualSeriesByTaskToMap(
      data?.projectActualSeriesByTaskForResourceUser
    )
  };
};

export const useProjectActualSeriesByTaskForUser = ({
  dateRange,
  projectId,
  scale,
  taskUris,
  userUri,
  skip
}) => {
  const {
    featureFlags: { isPsaRmpEnhancedProjectManagerPersonaEnabled }
  } = useMeContext();

  const periodResolution = mapScaleToPeriodResolution(scale);

  const legacyResourceUserResult = useLegacyProjectActualSeriesByTaskForUser({
    dateRange,
    projectId,
    periodResolution,
    taskUris,
    userUri,
    skip: skip || isPsaRmpEnhancedProjectManagerPersonaEnabled
  });

  const resourceUserResult = useProjectActualSeriesByTaskForResourceUser({
    dateRange,
    projectId,
    periodResolution,
    taskUris,
    userUri,
    skip: skip || !isPsaRmpEnhancedProjectManagerPersonaEnabled
  });

  return isPsaRmpEnhancedProjectManagerPersonaEnabled
    ? resourceUserResult
    : legacyResourceUserResult;
};

export default useProjectActualSeriesByTaskForUser;
